import { ReactNode } from 'react';

export interface RestaurantTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Clases extra para la celda (alineación, ancho, etc.). */
  className?: string;
  /** Clases extra para el `<th>` (por defecto hereda las de `className`). */
  headClassName?: string;
  cell: (row: T) => ReactNode;
}

interface RestaurantTableProps<T> {
  columns: RestaurantTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  /** Clases por fila (p. ej. atenuar inactivos). */
  rowClassName?: (row: T) => string;
  /** Ancho mínimo de la tabla antes de hacer scroll horizontal. */
  minWidth?: string;
}

/**
 * Tabla estándar de los listados del módulo de restaurante.
 * Se muestra solo en pantallas medianas o mayores (`hidden md:block`); en móvil
 * cada pantalla sigue mostrando su grilla de tarjetas.
 */
function RestaurantTable<T>({
  columns,
  rows,
  rowKey,
  rowClassName,
  minWidth = 'min-w-[1000px]',
}: RestaurantTableProps<T>) {
  return (
    <div className="hidden overflow-x-auto md:block">
      <table className={`w-full ${minWidth} table-auto`}>
        <thead>
          <tr className="border-b border-stroke bg-gray-2/60 text-left text-xs font-semibold uppercase tracking-wide text-bodydark2 dark:border-strokedark dark:bg-meta-4/30">
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-4 py-4 ${column.headClassName ?? column.className ?? ''}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={`border-b border-stroke align-middle last:border-0 dark:border-strokedark ${
                rowClassName?.(row) ?? ''
              }`}
            >
              {columns.map((column) => (
                <td key={column.key} className={`px-4 py-4 ${column.className ?? ''}`}>
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default RestaurantTable;
